import {Component, OnInit} from '@angular/core';
// tslint:disable-next-line:import-blacklist
import {Subject} from 'rxjs';
import {CatalogService} from '../catalog-api/catalog.service';
import {ToasterService} from 'angular2-toaster';
import {GeneralConstants} from '../../shared/general.constants';

const DEBOUNCE_TIME = 500;
const ES_RESULTS_SIZE = 20;
const ES_LARGER_SEARCH_AMOUNT = 100;

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'search-titles',
    templateUrl: 'search-titles.component.html',
    styleUrls: ['search-titles.component.scss']
})
export class SearchTitlesComponent implements OnInit {
    // Navigation
    private navIndex = 0;

    // Checkboxes
    private checkboxes = {
        show: true,
        movie: false,
        talent: false,
        sport: false,
        extended: false,
        demand: true,
        supply: false
    };
    private shouldDoSearch = false;

    // Checkboxes and values for original country/language/network filters
    private originalCheckboxes = {
        country: false,
        language: false,
        network: false,
        catalogState: false
    };
    private countrySubject = new Subject<string>();
    private originalCountry = '';
    private languageSubject = new Subject<string>();
    private originalLanguage = '';
    private networkSubject = new Subject<string>();
    private originalNetwork = '';
    private catalogStates;
    private catalogStateSubject = new Subject<string>();
    private catalogState = '';
    private contentType = 'tv';

    // Subject for search debounce and search value
    private subject = new Subject<string>();
    private searchValue = '';

    // Page booleans
    private searchLoading = false;

    // ES Results
    private results = [];
    private finalsResults = [];

    // Selected show for catalog-manage component
    private selectedShow;

    // Boolean for title creation
    private createTitleRequests = false;

    // Number of hits for the ES search and details for loading more
    private totalResultsMetadata = 0;
    private totalResultsImdb = 0;
    private from = 0;
    private largerSearch = false;
    private hasMore = false;

    constructor(private catalogService: CatalogService,
                private toasterService: ToasterService,
                private generalConstants: GeneralConstants) {
    }

    ngOnInit() {
        this.catalogStates = [''].concat(this.generalConstants.getCatalogStates());
        this.countrySubject.debounceTime(DEBOUNCE_TIME)
            .distinctUntilChanged()
            .subscribe(country => {
                this.shouldDoSearch = true;
                this.subject.next(this.searchValue);
            });
        this.languageSubject.debounceTime(DEBOUNCE_TIME)
            .distinctUntilChanged()
            .subscribe(language => {
                this.shouldDoSearch = true;
                this.subject.next(this.searchValue);
            });
        this.networkSubject.debounceTime(DEBOUNCE_TIME)
            .distinctUntilChanged()
            .subscribe(network => {
                this.shouldDoSearch = true;
                this.subject.next(this.searchValue);
            });
        this.catalogStateSubject.debounceTime(DEBOUNCE_TIME)
            .distinctUntilChanged()
            .subscribe(catalogState => {
                this.shouldDoSearch = true;
                this.subject.next(this.searchValue);
            });
        this.subject.debounceTime(DEBOUNCE_TIME)
            .distinctUntilChanged((prev, curr) => prev === curr && !this.shouldDoSearch)
            .subscribe(searchValue => {
                // Search value has changed, reset results, total results and from
                this.results = [];
                this.finalsResults = [];
                this.totalResultsImdb = 0;
                this.totalResultsMetadata = 0;
                this.from = 0;
                this.doSearch(searchValue);
            });
    }

    doSearch(searchValue) {
        this.shouldDoSearch = false;
        this.searchLoading = true;
        const typeFilters = Object.keys(this.checkboxes)
            .filter(item => item !== 'extended' && this.checkboxes[item]);
        const originalFilters = {};
        if (this.originalCheckboxes.country && this.originalCountry && this.originalCountry !== '') {
            originalFilters['data.original_country.*'] = this.originalCountry;
        }
        if (this.originalCheckboxes.language && this.originalLanguage && this.originalLanguage !== '') {
            originalFilters['data.original_language.*'] = this.originalLanguage;
        }
        if (this.originalCheckboxes.network && this.originalNetwork && this.originalNetwork !== '') {
            originalFilters['data.original_network.*'] = this.originalNetwork;
        }
        if (this.originalCheckboxes.catalogState && this.catalogState && this.catalogState !== '') {
            originalFilters['catalog_state'] = this.catalogState;
        }
        // If there are original filters, but no search value, use an unlimited (very large) search size
        this.largerSearch = false;
        if (Object.keys(originalFilters).length > 0 && searchValue === '') {
            this.largerSearch = true;
        }
        let queryJSON;

        let directQueryJson;

        if (this.checkboxes.extended && this.checkboxes.demand) {
            let catalogState = [];

            if (this.originalCheckboxes.catalogState && this.catalogState && this.catalogState !== '') {
                catalogState.push(this.catalogState);
            } else {
                catalogState = ['metadata_ready', 'harvested', 'requested', 'demand_generation', 'client_ready'];
            }
            queryJSON = this.catalogService.createDemandEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType
            );
            directQueryJson = this.catalogService.createDirectEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType
            );
        } else if (this.checkboxes.extended) {
            let catalogState = [];

            if (this.originalCheckboxes.catalogState && this.catalogState && this.catalogState !== '') {
                catalogState.push(this.catalogState);
            } else {
                catalogState = ['metadata_ready'];
            }

            queryJSON = this.catalogService.createDemandEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType
            );

            directQueryJson = this.catalogService.createDirectEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType);
        } else {
            let catalogState = [];

            if (this.originalCheckboxes.catalogState && this.catalogState && this.catalogState !== '') {
                catalogState.push(this.catalogState);
            } else {
                catalogState = ['harvested', 'requested', 'demand_generation', 'client_ready'];
            }

            queryJSON = this.catalogService.createDemandEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType
            );

            directQueryJson = this.catalogService.createDirectEsQuery(catalogState, true,
                searchValue, this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE, typeFilters, originalFilters,
                this.catalogService.getEsDefaultSource(), 'catalog', this.from, this.contentType);
        }

        {

            let index;

            if (this.contentType === 'tv') {
                index = 'series_metadata';
            }
            if (this.contentType === 'movie') {
                index = 'movie_metadata';

            }
            if (this.contentType === 'talent') {
                index = 'talent_metadata';

            }
            if (this.contentType === 'sports') {
                index = 'sports_metadata';

            }

            this.catalogService.callEsProd(
                directQueryJson,
                index,
            ).then((res) => {
                this.results = this.results.concat(res.hits.hits.map(item => this.catalogService.mapEsMetadataResultItem(item, this.contentType)));
                console.log('First result' + this.results);
            }).catch((err) => {
                console.error('Failed to call the ES proxy in the catalog API' + err.toString());
                this.toasterService.pop('error', 'Failed to call the ES proxy in the catalog API');
                this.searchLoading = false;
            });

            this.catalogService.callEsProd(
                queryJSON,
                index,
            ).then((res) => {
                this.results = this.results.concat(res.hits.hits.map(item => this.catalogService.mapEsMetadataResultItem(item, this.contentType)));
                this.finalsResults = this.results.filter((individualObject, index, self) =>
                    index === self.findIndex((t) => (
                        t.parrotId === individualObject.parrotId && t.title === individualObject.title
                    ))
                );

                this.totalResultsMetadata = res.hits.total.value;
                const pageSize = this.largerSearch ? ES_LARGER_SEARCH_AMOUNT : ES_RESULTS_SIZE;
                if (this.totalResultsMetadata > (this.from + pageSize)) {
                    this.hasMore = true;
                } else {
                    this.hasMore = false;
                }
                this.searchLoading = false;
            }).catch((err) => {
                console.error('Failed to call the ES proxy in the catalog API' + err.toString());
                console.log(err)
                this.toasterService.pop('error', 'Failed to call the ES proxy in the catalog API');
                this.searchLoading = false;
            });
        }
    }

    handleLoadMore() {
        if (this.largerSearch) {
            this.from = this.from + ES_LARGER_SEARCH_AMOUNT;
        } else {
            this.from = this.from + ES_RESULTS_SIZE;
        }
        this.doSearch(this.searchValue);
    }

    handleNavSelect(index) {
        this.navIndex = index;
    }

    handleSearchCheckbox(checkboxName) {
        // Check that there will be at least one checkbox selectd
        // const willBeCheckboxSelected = Object.keys(this.checkboxes)
        //     .map(item => [item, this.checkboxes[item]])
        //     .filter(item => item[1] && item[0] !== checkboxName && item[0] !== 'extended')
        //     .length > 0;
        // if (willBeCheckboxSelected) {
        if (checkboxName !== 'demand' && checkboxName !== 'extended' ) {
            if (checkboxName === 'movie' && !this.checkboxes.movie) {
                this.contentType = 'movie';
                this.checkboxes.show = false;
                this.checkboxes.talent = false;
                this.checkboxes.sport = false;
            } else if (checkboxName === 'talent' && !this.checkboxes.talent) {
                this.contentType = 'talent';
                this.checkboxes.show = false;
                this.checkboxes.movie = false;
                this.checkboxes.sport = false;
            } else if (checkboxName === 'sport' && !this.checkboxes.sport) {
                this.contentType = 'sports';
                this.checkboxes.movie = false;
                this.checkboxes.talent = false;
                this.checkboxes.show = false;
            } else {
                this.contentType = 'tv';
                this.checkboxes.movie = false;
                this.checkboxes.talent = false;
                this.checkboxes.sport = false;
            }
        }
        setTimeout(() => {
            this.checkboxes[checkboxName] = !this.checkboxes[checkboxName];
            this.shouldDoSearch = true;
            if (this.searchValue) {
                this.subject.next(this.searchValue);
            }
        });
    }

    handleOriginalFiltersCheckboxes(checkboxName) {
        this.originalCheckboxes[checkboxName] = !this.originalCheckboxes[checkboxName];
        // If a recently enabled checkbox has text, do a search.)
        if (this.shouldSearch()) {
            switch (checkboxName) {
                case 'language':
                    if (this.originalLanguage && this.originalLanguage !== '') {
                        this.shouldDoSearch = true;
                        this.subject.next(this.searchValue);
                    }
                    break;
                case 'network':
                    if (this.originalNetwork && this.originalNetwork !== '') {
                        this.shouldDoSearch = true;
                        this.subject.next(this.searchValue);
                    }
                    break;
                case 'country':
                    if (this.originalCountry && this.originalCountry !== '') {
                        this.shouldDoSearch = true;
                        this.subject.next(this.searchValue);
                    }
                    break;
                case 'catalogState':
                    if (this.catalogState && this.catalogState !== '') {
                        this.shouldDoSearch = true;
                        this.subject.next(this.searchValue);
                    }
                    break;
            }
        } else {
            // If all filters are disabled, set the table data to empty
            this.results = [];
        }
    }

    handleOpen(result) {
        if (!this.selectedShow || (this.selectedShow && this.selectedShow.parrot_id !== result.parrotId)) {
            this.selectedShow = {
                parrot_id: result.parrotId,
                title: result.title,
                display_name: result.displayName,
                content_type: result.contentType,
                catalog_state: result.catalogState
            };
        } else if (this.selectedShow && this.selectedShow.parrot_id === result.parrotId) {
            // Trigger a change for the property
            setTimeout(() => {
                this.selectedShow = {
                    parrot_id: undefined,
                    catalog_state: undefined,
                    title: undefined,
                    content_type: undefined,
                    display_name: undefined
                };
            });
            setTimeout(() => {
                this.selectedShow = {
                    parrot_id: result.parrotId,
                    title: result.title,
                    display_name: result.displayName,
                    content_type: result.contentType,
                    catalog_state: result.catalogState
                };
            });
        }
    }

    handleCreateTitle() {
        this.createTitleRequests = true;
        setTimeout(() => {
            this.createTitleRequests = false;
        }, 1000);
    }

    onKeyUp(searchValue) {
        this.searchValue = searchValue;
        if (this.shouldSearch()) {
            this.subject.next(searchValue);
        } else {
            this.results = [];
        }
    }

    onOriginalKeyUp(value, type) {
        switch (type) {
            case 'language':
                this.originalLanguage = value;
                this.languageSubject.next(value);
                break;
            case 'network':
                this.originalNetwork = value;
                this.networkSubject.next(value);
                break;
            case 'country':
                this.originalCountry = value;
                this.countrySubject.next(value);
                break;
            case 'catalogState':
                if (value) {
                    this.catalogState = value;
                    this.catalogStateSubject.next(value);
                }
                break;
        }
    }

    shouldSearch() {
        return this.searchValue || (this.originalCheckboxes.country ||
            this.originalCheckboxes.language || this.originalCheckboxes.network);
    }
}



// WEBPACK FOOTER //
// ./src/app/catalogue/search-titles/search-titles.component.ts